'use client'

import dynamic from 'next/dynamic'
import { useState, useEffect } from 'react'
import { usePathname } from 'next/navigation'
import { useScrollDirection } from '@/hooks/useScrollDirection'
import { Settings, Save, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/Button'
// import LanguageSwitcher from './LanguageSwitcher' // Disabled: Only English supported
import LoginButton from './LoginButton'
import Logo from './Logo'
import ThemeToggle from './ThemeToggle'
import HamburgerButton from './HamburgerButton'
import { CART_DRAWER_EVENT } from '@/utils/cartDrawer'

const NotificationBellButton = dynamic(() => import('./NotificationBellButton'))
const ProfilePicture = dynamic(() => import('./ProfilePicture'))
const CartIcon = dynamic(() => import('@/components/Cart/CartIcon'))
const CartDrawer = dynamic(() => import('@/components/Cart/CartDrawer'))
const NotificationDrawer = dynamic(
  () => import('@/components/Notifications/NotificationDrawer')
)

interface BuilderActions {
  onSave: () => void
  onDiscard: () => void
  isSaving?: boolean
  isDeleting?: boolean
  hasUnsavedChanges?: boolean
}

interface GlobalHeaderProps {
  onMobileMenuClick?: () => void
  onMobileMenuClose?: () => void // Close mobile menu (e.g., when cart/profile clicked)
  isMobileMenuOpen?: boolean
  onAppSettingsClick?: () => void
  isAppSettingsOpen?: boolean
  isAuthenticated?: boolean
  onLoginClick?: () => void
  builderActions?: BuilderActions // Builder-specific actions
}

export default function GlobalHeader({
  onMobileMenuClick,
  onMobileMenuClose,
  isMobileMenuOpen = false,
  onAppSettingsClick,
  isAppSettingsOpen = false,
  isAuthenticated = false,
  onLoginClick: _onLoginClick,
  builderActions,
}: GlobalHeaderProps) {
  void _onLoginClick
  const pathname = usePathname()
  const scrollDirection = useScrollDirection()
  const [isCartOpen, setIsCartOpen] = useState(false)
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false)
  const [shouldRenderCartDrawer, setShouldRenderCartDrawer] = useState(false)
  const [shouldRenderNotificationDrawer, setShouldRenderNotificationDrawer] =
    useState(false)

  const handleOpenCart = () => {
    setShouldRenderCartDrawer(true)
    setIsCartOpen(true)
  }

  const handleOpenNotifications = () => {
    setShouldRenderNotificationDrawer(true)
    setIsNotificationsOpen(true)
  }

  useEffect(() => {
    const handleOpenCartDrawer = () => handleOpenCart()
    window.addEventListener(CART_DRAWER_EVENT, handleOpenCartDrawer)
    return () =>
      window.removeEventListener(CART_DRAWER_EVENT, handleOpenCartDrawer)
  }, [])

  const isHomePage = pathname === '/'
  const isHidden = isHomePage && scrollDirection === 'down'

  const isBuilderPage =
    pathname.startsWith('/builder') ||
    pathname.startsWith('/stationery-builder')

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 h-14 bg-card border-b border-border shadow-sm transition-transform duration-300 will-change-transform ${
        isHidden ? '-translate-y-full' : 'translate-y-0'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 lg:px-10 h-full">
        <nav className="flex items-center justify-between h-full">
          <div className="flex items-center gap-1 md:hidden">
            {onMobileMenuClick ? (
              <HamburgerButton
                onClick={onMobileMenuClick}
                isOpen={isMobileMenuOpen}
              />
            ) : (
              <div className="w-8 h-8" />
            )}
            {isAuthenticated && (
              <NotificationBellButton
                size="mobile"
                onClick={() => {
                  onMobileMenuClose?.()
                  handleOpenNotifications()
                }}
              />
            )}
          </div>

          <div className="absolute left-1/2 -translate-x-1/2 md:relative md:left-0 md:translate-x-0">
            <Logo />
          </div>

          {isBuilderPage && builderActions ? (
            <div className="hidden md:flex flex-none gap-2 sm:gap-3">
              <Button
                onClick={builderActions.onSave}
                disabled={
                  builderActions.isSaving ||
                  builderActions.isDeleting ||
                  !builderActions.hasUnsavedChanges
                }
                className="gap-1.5"
              >
                {builderActions.isSaving ? (
                  <>
                    <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-3.5 h-3.5" />
                    Save
                  </>
                )}
              </Button>

              <Button
                onClick={builderActions.onDiscard}
                disabled={builderActions.isSaving || builderActions.isDeleting}
                variant="destructive"
                size="icon"
                className="px-3"
                aria-label="Discard"
              >
                {builderActions.isDeleting ? (
                  <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Trash2 className="w-3.5 h-3.5" />
                )}
              </Button>
            </div>
          ) : (
            <div className="hidden md:flex flex-none gap-3 items-center">
              {isAuthenticated ? (
                <>
                  <NotificationBellButton onClick={handleOpenNotifications} />
                  <CartIcon onClick={handleOpenCart} />
                  <ProfilePicture />
                </>
              ) : (
                <>
                  {/* <LanguageSwitcher /> */}
                  <ThemeToggle iconOnly />
                  <LoginButton />
                </>
              )}
            </div>
          )}

          {isBuilderPage && builderActions ? (
            <div className="flex md:hidden gap-1.5 sm:gap-2">
              <Button
                onClick={builderActions.onSave}
                disabled={
                  builderActions.isSaving ||
                  builderActions.isDeleting ||
                  !builderActions.hasUnsavedChanges
                }
                size="sm"
                className="gap-1.5 text-xs"
              >
                {builderActions.isSaving ? (
                  <>
                    <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span className="hidden xs:inline">Saving...</span>
                  </>
                ) : (
                  <>
                    <Save className="w-3.5 h-3.5" />
                    <span className="hidden xs:inline">Save</span>
                  </>
                )}
              </Button>

              <Button
                onClick={builderActions.onDiscard}
                disabled={builderActions.isSaving || builderActions.isDeleting}
                variant="destructive"
                size="sm"
                className="px-2.5"
                aria-label="Discard"
              >
                {builderActions.isDeleting ? (
                  <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Trash2 className="w-3.5 h-3.5" />
                )}
              </Button>
            </div>
          ) : (
            <div className="md:hidden flex items-center gap-2">
              {isAuthenticated ? (
                <>
                  <CartIcon
                    onClick={() => {
                      onMobileMenuClose?.()
                      handleOpenCart()
                    }}
                  />
                  <ProfilePicture onOpen={onMobileMenuClose} />
                </>
              ) : (
                <button
                  onClick={onAppSettingsClick}
                  className="p-2 rounded-lg text-foreground hover:bg-muted hover:text-primary transition-colors duration-150 touch-manipulation active:bg-muted/80 cursor-pointer"
                  aria-label="Settings"
                >
                  <Settings
                    className={`w-5 h-5 transition-transform duration-300 ${
                      isAppSettingsOpen ? 'rotate-90' : ''
                    }`}
                  />
                </button>
              )}
            </div>
          )}
        </nav>
      </div>

      {isAuthenticated && (
        <>
          {shouldRenderNotificationDrawer && (
            <NotificationDrawer
              isOpen={isNotificationsOpen}
              onClose={() => setIsNotificationsOpen(false)}
            />
          )}
          {shouldRenderCartDrawer && (
            <CartDrawer
              isOpen={isCartOpen}
              onClose={() => setIsCartOpen(false)}
            />
          )}
        </>
      )}
    </header>
  )
}
